/**
 * DeepSeaIndicator
 *
 * 深海領域の状態を表示するインジケータコンポーネント
 * - 開放状態: カード数/上限を表示
 * - 未開放時: 非表示
 * - クリックでモーダルを開く
 */

import React from 'react';
import type { DeepSeaZone } from '../../engine/deepSeaZone/types';

interface DeepSeaIndicatorProps {
  /** 深海領域の状態 */
  deepSeaZone: DeepSeaZone;
  /** クリック時のコールバック */
  onClick: () => void;
  /** 自分の深海領域かどうか */
  isOwn: boolean;
  /** モバイル表示かどうか */
  isMobile?: boolean;
}

const DeepSeaIndicator: React.FC<DeepSeaIndicatorProps> = ({
  deepSeaZone,
  onClick,
  isOwn,
  isMobile = false,
}) => {
  // 未開放時は非表示
  if (!deepSeaZone.isUnlocked) {
    return null;
  }

  const cardCount = deepSeaZone.cards.length;
  const maxCapacity = deepSeaZone.maxCapacity;
  const isEmpty = cardCount === 0;

  // スタイル
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '4px' : '6px',
    padding: isMobile ? '4px 8px' : '6px 12px',
    background: isEmpty
      ? 'linear-gradient(135deg, rgba(30, 60, 90, 0.6), rgba(20, 40, 60, 0.6))'
      : 'linear-gradient(135deg, rgba(0, 100, 150, 0.8), rgba(0, 60, 100, 0.8))',
    borderRadius: '8px',
    border: `1px solid ${isEmpty ? 'rgba(100, 150, 200, 0.3)' : 'rgba(0, 200, 255, 0.5)'}`,
    cursor: isOwn ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    boxShadow: isEmpty
      ? 'none'
      : '0 0 10px rgba(0, 150, 255, 0.3)',
  };

  const iconStyle: React.CSSProperties = {
    fontSize: isMobile ? '14px' : '16px',
  };

  const textStyle: React.CSSProperties = {
    color: isEmpty ? '#8ab4d4' : '#4dd0e1',
    fontSize: isMobile ? '11px' : '12px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
  };

  const countStyle: React.CSSProperties = {
    color: cardCount >= maxCapacity ? '#ff9800' : '#4dd0e1',
    fontWeight: 'bold',
  };

  return (
    <div
      style={containerStyle}
      onClick={isOwn ? onClick : undefined}
      onMouseEnter={(e) => {
        if (isOwn) {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 150, 255, 0.5)';
        }
      }}
      onMouseLeave={(e) => {
        if (isOwn) {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = isEmpty ? 'none' : '0 0 10px rgba(0, 150, 255, 0.3)';
        }
      }}
      title={isOwn ? 'クリックで深海領域を表示' : `相手の深海領域: ${cardCount}枚`}
    >
      <span style={iconStyle}>🌊</span>
      <span style={textStyle}>
        {isOwn ? (
          <>
            深海 <span style={countStyle}>{cardCount}/{maxCapacity}</span>
          </>
        ) : (
          <>
            深海 <span style={countStyle}>{cardCount}枚</span>
          </>
        )}
      </span>
    </div>
  );
};

export default DeepSeaIndicator;
